import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@eye-able/ui';
import { useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  useCreatePolicy,
  type PolicyResponse,
} from '@http/queries/useCreatePolicy';
import { useFetchPolicy } from '@http/queries/useFetchPolicy';
import LoadingSpinner from '@components/LoadingSpinner';
import PolicyForm, {
  blankPolicyInitialValues,
  policyToInitialValues,
  templateInitialValues,
} from './PolicyForm';
import type { PolicyTemplate } from './PolicyTemplateLibrary';
import { parsePolicyId } from './policyId';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';
import { usePolicyRescan } from './PolicyRescanControl';
import { useGovernanceAccess } from '@/hooks/useGovernanceAccess';
import { useEditableGovernanceDomainOptions } from './governanceDomainOptions';

function NewPolicyContent() {
  const { t } = useInlineTranslation();
  const governanceAccess = useGovernanceAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const createPolicy = useCreatePolicy();
  const { rescanPolicy } = usePolicyRescan();
  const [createdPolicy, setCreatedPolicy] = useState<PolicyResponse | null>(
    null,
  );

  // When arriving from "Use template" in the library, the chosen template is
  // passed via router state and used to prefill the form.
  const template = (location.state as { template?: PolicyTemplate } | null)
    ?.template;

  // When arriving from "Duplicate" on a policy card, the source policy id is
  // passed via the query string and its values prefill the form.
  const duplicateId = parsePolicyId(searchParams.get('duplicateId'));
  const { data: duplicatedPolicy, isLoading: isDuplicateLoading } =
    useFetchPolicy(duplicateId);
  const domainOptions = useEditableGovernanceDomainOptions(
    duplicatedPolicy?.auditEntryIds ?? [],
  );

  const initialValues = useMemo(() => {
    if (duplicatedPolicy) {
      const values = policyToInitialValues(duplicatedPolicy);
      return { ...values, name: t('copyName', { name: values.name }) };
    }
    if (template) return templateInitialValues(template, t);
    return blankPolicyInitialValues();
  }, [duplicatedPolicy, t, template]);

  if (governanceAccess.isLoading) {
    return null;
  }

  if (!governanceAccess.canEdit) {
    return <Navigate to='/policies' replace />;
  }

  // Wait for the source policy before rendering, so the form mounts prefilled.
  if (duplicateId != null && isDuplicateLoading) {
    return <LoadingSpinner />;
  }

  const navigateToPolicies = () => {
    void navigate('/policies');
  };

  return (
    <>
      <PolicyForm
        heading={t('newPolicy')}
        description={t('newPolicyDescription')}
        submitLabel={t('createPolicy')}
        canSaveAsDraft
        isSubmitting={createPolicy.isPending}
        initialValues={initialValues}
        domainOptions={domainOptions}
        onSubmit={(body) => {
          void createPolicy.mutate(body, {
            onSuccess: (policy) => {
              void rescanPolicy(policy, { showSuccessNotification: false });
              setCreatedPolicy(policy);
            },
          });
        }}
      />
      <Dialog
        open={createdPolicy !== null}
        onOpenChange={(open) => {
          if (!open) navigateToPolicies();
        }}
      >
        <DialogContent maxWidth='md'>
          <DialogTitle>{t('policyCreated')}</DialogTitle>
          <DialogActions>
            <Button
              type='button'
              variant='outline'
              onClick={() => navigateToPolicies()}
            >
              {t('viewPolicies')}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NewPolicy() {
  return (
    <GovernanceAccessBoundary>
      <NewPolicyContent />
    </GovernanceAccessBoundary>
  );
}

export default NewPolicy;
